import { useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetch_notes } from '../redux/note-slice';
import { fetch_lessons_by_tutor_id } from '../redux/lesson-slice';
import { fetch_current_student } from '../redux/student-slice';
import { fetch_current_user } from '../redux/user-slice';
import { fetch_tutor_availability, fetch_tutor_schedule } from '../redux/schedule-slice';
import { fetchStudentAnalytics, fetchTutorAnalytics } from '../redux/analytics-slice';
import { fetchUserGamificationData } from '../redux/gamification-slice';
import { fetch_user_profile } from '../redux/auth-slice';
import { fetch_current_tutor } from '../redux/tutor-slice';
import { fetchConversations } from '../redux/message-slice';
import { fetchQuizTemplates } from '../redux/quiz-slice';
import { fetchActiveGradingParameters, fetchAllGradingParameters } from '../redux/grading-slice';
import { get_meetings } from '../redux/meeting-slice';
import { fetch_student_subscriptions, fetch_tutor_subscriptions } from '../redux/payment-slice';
import { get_classrooms_by_tutor } from '../redux/classroom-slice';
import { fetchStudentEngagementLevels } from '../redux/engagement-slice';
import { fetchStudentInterventions, fetchTutorWarnings } from '../redux/progress-monitoring-slice';
import { fetchLibraryTree } from '../redux/resource-library-slice';
import { useSmartCache } from './useSmartCache';
import { debounce } from '../utils/retryUtils';
import { CLIENT_TTL_MS } from '../config/cache-ttl';
import { store } from '../redux/store';
import { isReduxUserAlignedWithSession } from '../utils/auth-session-utils';
import { isOnboardingRoute } from '../utils/onboarding-route';
/** Read latest auth user from the store (avoids stale closures after fetch_user_profile). */
function getFreshAuthUser() {
    return store.getState().auth.user;
}
/** Run async tasks in fixed-size batches to avoid bursting the API (502 under concurrent load). */
async function runInBatches(tasks, batchSize) {
    for (let i = 0; i < tasks.length; i += batchSize) {
        const slice = tasks.slice(i, i + batchSize).map((fn) => fn());
        await Promise.all(slice);
    }
}
/**
 * Data Persistence Hook
 *
 * Provides centralized data refresh functionality to ensure consistent data display
 * across the application. This hook handles the common pattern of refreshing
 * related data after successful save operations.
 */
export const useDataPersistence = () => {
    const dispatch = useAppDispatch();
    const { user, isAuthenticated } = useAppSelector((state) => state.auth);
    // Smart cache for data staleness tracking (default TTL aligned with shared config)
    const cache = useSmartCache({
        defaultTtl: CLIENT_TTL_MS.notes,
        maxSize: 100,
        enableDebug: false
    });
    // Track last fetch times
    const lastFetchTimes = useRef({});
    // Check if data should be refreshed based on staleness (use CLIENT_TTL_MS from shared config)
    const shouldRefresh = useCallback((dataType, ttl) => {
        const effectiveTtl = ttl ?? 5 * 60 * 1000;
        const lastFetch = lastFetchTimes.current[dataType] || 0;
        return Date.now() - lastFetch > effectiveTtl;
    }, []);
    // Update last fetch time
    const updateLastFetchTime = useCallback((dataType) => {
        lastFetchTimes.current[dataType] = Date.now();
    }, []);
    /**
     * Refresh user profile data
     * @param force - If true, skip TTL check and always refetch (e.g. after save)
     */
    const refreshUserData = useCallback(async (force) => {
        const authUser = getFreshAuthUser();
        if (!isAuthenticated || !authUser?.id)
            return;
        if (!force && !shouldRefresh('userData', CLIENT_TTL_MS.userData))
            return;
        try {
            const profileResult = await dispatch(fetch_user_profile({
                userId: authUser.id,
                userRole: authUser.role || 'student',
                userEmail: authUser.email
            }));
            if (fetch_user_profile.rejected.match(profileResult)) {
                if (force) {
                    throw new Error(profileResult.payload || 'Failed to refresh user profile');
                }
                return;
            }
            const freshUser = getFreshAuthUser();
            if (!freshUser?.id)
                return;
            if (freshUser.role === 'student') {
                await dispatch(fetch_current_student());
            }
            else if (freshUser.role === 'tutor') {
                await dispatch(fetch_current_user());
                await dispatch(fetch_current_tutor());
            }
            updateLastFetchTime('userData');
        }
        catch (error) {
            console.error('❌ Error refreshing user profile data:', error);
            if (force)
                throw error;
        }
    }, [dispatch, isAuthenticated, shouldRefresh, updateLastFetchTime]);
    /**
     * Refresh notes data
     * @param force - If true, skip TTL and always refetch (e.g. after save)
     */
    const refreshNotes = useCallback(async (force) => {
        if (!isAuthenticated || !user?.id)
            return;
        if (!force && !shouldRefresh('notes', CLIENT_TTL_MS.notes))
            return;
        try {
            await dispatch(fetch_notes('all'));
            updateLastFetchTime('notes');
        }
        catch (error) {
            console.error('❌ Error refreshing notes data:', error);
            if (force)
                throw error;
        }
    }, [dispatch, isAuthenticated, user?.id, shouldRefresh, updateLastFetchTime]);
    /**
     * Refresh lessons data for tutors
     * @param force - If true, skip TTL and always refetch (e.g. after save)
     */
    const refreshLessons = useCallback(async (force) => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || authUser.role !== 'tutor')
            return;
        if (!force && !shouldRefresh('lessons', CLIENT_TTL_MS.lessons))
            return;
        try {
            await dispatch(fetch_lessons_by_tutor_id({ tutorId: authUser.id }));
            updateLastFetchTime('lessons');
        }
        catch (error) {
            console.error('❌ Error refreshing lessons data:', error);
            if (force)
                throw error;
        }
    }, [dispatch, shouldRefresh, updateLastFetchTime]);
    /**
     * Refresh schedule/availability data for tutors
     * Called after availability updates
     */
    const refreshSchedule = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || authUser.role !== 'tutor')
            return;
        try {
            await dispatch(fetch_tutor_availability(authUser.id));
            await dispatch(fetch_tutor_schedule(authUser.id));
        }
        catch (error) {
            console.error('❌ Error refreshing schedule data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh analytics data
     * Called after data changes that affect analytics
     */
    const refreshAnalytics = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || !authUser.role)
            return;
        if (!isReduxUserAlignedWithSession(authUser.id))
            return;
        try {
            if (authUser.role === 'student') {
                await dispatch(fetchStudentAnalytics({ studentId: authUser.id }));
            }
            else if (authUser.role === 'tutor') {
                await dispatch(fetchTutorAnalytics({ tutorId: authUser.id, timeFrame: 'week' }));
            }
        }
        catch (error) {
            console.error('❌ Error refreshing analytics data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh gamification data
     * Called after activities that affect points, badges, or streaks
     */
    const refreshGamification = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id)
            return;
        if (!isReduxUserAlignedWithSession(authUser.id))
            return;
        try {
            await dispatch(fetchUserGamificationData(undefined));
        }
        catch (error) {
            console.error('❌ Error refreshing gamification data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh messaging data
     * Called after message sending, conversation updates
     */
    const refreshMessaging = useCallback(async (force) => {
        if (!user?.id)
            return;
        if (!force && !shouldRefresh('messaging', CLIENT_TTL_MS.messaging))
            return;
        try {
            await dispatch(fetchConversations());
            updateLastFetchTime('messaging');
        }
        catch (error) {
            console.error('❌ Error refreshing messaging data:', error);
            if (force)
                throw error;
        }
    }, [dispatch, user?.id, shouldRefresh, updateLastFetchTime]);
    /**
     * Refresh quiz data
     * @param force - If true, skip TTL and always refetch
     */
    const refreshQuizzes = useCallback(async (force) => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || authUser.role !== 'tutor')
            return;
        if (!force && !shouldRefresh('quizzes', CLIENT_TTL_MS.quizzes))
            return;
        try {
            await dispatch(fetchQuizTemplates());
            updateLastFetchTime('quizzes');
        }
        catch (error) {
            console.error('❌ Error refreshing quiz data:', error);
            if (force)
                throw error;
        }
    }, [dispatch, shouldRefresh, updateLastFetchTime]);
    /**
     * Refresh rubric and assessment data
     * Called after assessment creation, grading, or rubric updates
     * - fetchActiveGradingParameters: admin + tutor only (API returns 403 for students)
     * - fetchAllGradingParameters: admin only (list/history of all configs)
     */
    const refreshRubrics = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id)
            return;
        if (authUser.role !== 'tutor' && authUser.role !== 'admin')
            return;
        try {
            await dispatch(fetchActiveGradingParameters());
            if (authUser.role === 'admin') {
                await dispatch(fetchAllGradingParameters());
            }
        }
        catch (error) {
            console.error('❌ Error refreshing rubric and assessment data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh payment data
     * @param force - If true, skip TTL and always refetch (e.g. after save or when stale)
     * Called after payment processing, transaction updates
     */
    const refreshPayments = useCallback(async (force) => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id)
            return;
        if (!isReduxUserAlignedWithSession(authUser.id))
            return;
        if (!force && !shouldRefresh('payments', CLIENT_TTL_MS.payments))
            return;
        try {
            if (authUser.role === 'student') {
                await dispatch(fetch_student_subscriptions());
            }
            else if (authUser.role === 'tutor') {
                await dispatch(fetch_tutor_subscriptions());
            }
            updateLastFetchTime('payments');
        }
        catch (error) {
            console.error('❌ Error refreshing payment data:', error);
            if (force)
                throw error;
        }
    }, [dispatch, shouldRefresh, updateLastFetchTime]);
    /**
     * Refresh meeting data
     * Called after meeting creation, updates, or completion
     */
    const refreshMeetings = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || (authUser.role !== 'student' && authUser.role !== 'tutor')) {
            return;
        }
        if (isOnboardingRoute()) {
            return;
        }
        try {
            await dispatch(get_meetings());
        }
        catch (error) {
            console.error('❌ Error refreshing meeting data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh classroom data
     * Called after classroom updates, student additions, or content changes
     */
    const refreshClassrooms = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || authUser.role !== 'tutor')
            return;
        try {
            await dispatch(get_classrooms_by_tutor());
        }
        catch (error) {
            console.error('❌ Error refreshing classroom data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh engagement data
     * Called after engagement activities or level changes
     */
    const refreshEngagement = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id || authUser.role !== 'student')
            return;
        if (!isReduxUserAlignedWithSession(authUser.id))
            return;
        try {
            await dispatch(fetchStudentEngagementLevels(authUser.id));
        }
        catch (error) {
            console.error('❌ Error refreshing engagement data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh progress monitoring data
     * Called after progress updates or milestone achievements
     */
    const refreshProgress = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!authUser?.id)
            return;
        if (!isReduxUserAlignedWithSession(authUser.id))
            return;
        try {
            if (authUser.role === 'student') {
                await dispatch(fetchStudentInterventions({ studentId: authUser.id }));
            }
            else if (authUser.role === 'tutor') {
                await dispatch(fetchTutorWarnings({ tutorId: authUser.id }));
            }
        }
        catch (error) {
            console.error('❌ Error refreshing progress monitoring data:', error);
            throw error;
        }
    }, [dispatch]);
    /**
     * Refresh resource library data
     * Called after resource additions, updates, or access changes
     */
    const refreshResourceLibrary = useCallback(async () => {
        if (user?.id) {
            try {
                await dispatch(fetchLibraryTree());
            }
            catch (error) {
                console.error('❌ Error refreshing resource library data:', error);
                throw error;
            }
        }
    }, [dispatch, user?.id]);
    /**
     * Refresh all data for the current user
     * Used for comprehensive data refresh after major operations
     */
    const refreshAllData = useCallback(async () => {
        try {
            await refreshUserData(true);
            await runInBatches([
                () => refreshNotes(),
                () => refreshLessons(),
                () => refreshSchedule(),
                () => refreshAnalytics(),
                () => refreshGamification(),
                () => refreshMessaging(),
                () => refreshQuizzes(),
                () => refreshRubrics(),
                () => refreshPayments(),
                () => refreshMeetings(),
                () => refreshClassrooms(),
                () => refreshEngagement(),
                () => refreshProgress(),
                () => refreshResourceLibrary(),
            ], 4);
        }
        catch (error) {
            console.error('❌ Error refreshing all data:', error);
            throw error;
        }
    }, [
        refreshUserData,
        refreshNotes,
        refreshLessons,
        refreshSchedule,
        refreshAnalytics,
        refreshGamification,
        refreshMessaging,
        refreshQuizzes,
        refreshRubrics,
        refreshPayments,
        refreshMeetings,
        refreshClassrooms,
        refreshEngagement,
        refreshProgress,
        refreshResourceLibrary,
    ]);
    /**
     * Refresh data based on user role
     * Automatically refreshes appropriate data based on user type
     */
    const refreshRoleBasedData = useCallback(async () => {
        const authUser = getFreshAuthUser();
        if (!isAuthenticated || !authUser?.id) {
            return;
        }
        try {
            // Profile first so role-specific endpoints use server-confirmed role
            await Promise.all([
                refreshUserData(true),
                refreshNotes()
            ]);
            const freshUser = getFreshAuthUser();
            if (freshUser?.role === 'tutor') {
                await Promise.all([
                    refreshLessons(),
                    refreshSchedule(),
                    refreshClassrooms()
                ]);
            }
            await runInBatches([
                () => refreshAnalytics(),
                () => refreshGamification(),
                () => refreshMessaging(),
                () => refreshQuizzes(),
                () => refreshRubrics(),
                () => refreshPayments(),
                () => refreshMeetings(),
                () => refreshEngagement(),
                () => refreshProgress(),
                () => refreshResourceLibrary(),
            ], 4);
        }
        catch (error) {
            console.error('❌ Error refreshing role-based data:', error);
            throw error;
        }
    }, [
        isAuthenticated,
        refreshUserData,
        refreshNotes,
        refreshLessons,
        refreshSchedule,
        refreshAnalytics,
        refreshGamification,
        refreshMessaging,
        refreshQuizzes,
        refreshRubrics,
        refreshPayments,
        refreshMeetings,
        refreshClassrooms,
        refreshEngagement,
        refreshProgress,
        refreshResourceLibrary
    ]);
    // Create debounced versions of refresh functions to prevent excessive API calls
    const debouncedRefreshUserData = useCallback(debounce(refreshUserData, 300), [refreshUserData]);
    const debouncedRefreshNotes = useCallback(debounce(refreshNotes, 300), [refreshNotes]);
    const debouncedRefreshLessons = useCallback(debounce(refreshLessons, 300), [refreshLessons]);
    const debouncedRefreshSchedule = useCallback(debounce(refreshSchedule, 300), [refreshSchedule]);
    const debouncedRefreshMessaging = useCallback(debounce(refreshMessaging, 300), [refreshMessaging]);
    const debouncedRefreshQuizzes = useCallback(debounce(refreshQuizzes, 300), [refreshQuizzes]);
    const debouncedRefreshPayments = useCallback(debounce(refreshPayments, 300), [refreshPayments]);
    return {
        // Core refresh functions
        refreshUserData,
        refreshNotes,
        refreshLessons,
        refreshSchedule,
        refreshAnalytics,
        refreshGamification,
        // New feature refresh functions
        refreshMessaging,
        refreshQuizzes,
        refreshRubrics,
        refreshPayments,
        refreshMeetings,
        refreshClassrooms,
        refreshEngagement,
        refreshProgress,
        refreshResourceLibrary,
        // Comprehensive refresh functions
        refreshAllData,
        refreshRoleBasedData,
        // Debounced versions for performance optimization
        debouncedRefreshUserData,
        debouncedRefreshNotes,
        debouncedRefreshLessons,
        debouncedRefreshSchedule,
        debouncedRefreshMessaging,
        debouncedRefreshQuizzes,
        debouncedRefreshPayments,
        // Utility functions
        shouldRefresh,
        updateLastFetchTime
    };
};
